import { Query } from '../types/definitionsType';
import { useLogger } from '@agyletime/vue-3-common/src/composables/logger';
import {
    updateFilterState, loadFilterState, type FilterState
} from './filterSessionStorage';

/**
 * Keys that should be stored in sessionStorage instead of the URL.
 * This prevents HTTP 414 errors from large filter payloads (e.g. 2772 userIds).
 */
const SESSION_STORAGE_KEYS = new Set([
    'facets', 'columnDisplay', 'dateRange', 'searchDateFilter'
]);

/**
 * Try to JSON.parse a value; return the original string on failure.
 */
function tryParseJson(value: string): unknown {
    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
}

/** Keys whose values should be JSON-parsed before storing in sessionStorage. */
const JSON_PARSE_KEYS = new Set([ 'facets', 'columnDisplay' ]);

function buildSessionValue(key: string, value: string | undefined): unknown {
    if (value === undefined) return undefined;
    return JSON_PARSE_KEYS.has(key) ? tryParseJson(value) : value;
}

function handleSessionStorageKey(
    key: string,
    value: string | undefined,
    url: URL,
    sessionUpdates: Record<string, unknown>
): void {
    sessionUpdates[key] = buildSessionValue(key, value);

    // Migration cleanup: remove from URL if it was previously there
    if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
    }
}

function handleUrlKey(key: string, value: string | undefined, url: URL): void {
    if (value !== undefined && url.searchParams.get(key) !== value) {
        url.searchParams.set(key, value);
    } else if (value === undefined && url.searchParams.has(key)) {
        url.searchParams.delete(key);
    }
}

/**
 * Update URL params for safe keys and delegate heavy data to sessionStorage.
 * This is a drop-in replacement — callers don't need to know which storage is used.
 */
export function updateUrlParams(filters: Record<string, string | undefined>): void {
    const url = new URL(window.location.href);
    const sessionUpdates: Record<string, unknown> = {};

    Object.entries(filters).forEach(([ key, value ]) => {
        if (SESSION_STORAGE_KEYS.has(key)) {
            handleSessionStorageKey(key, value, url, sessionUpdates);
        } else {
            handleUrlKey(key, value, url);
        }
    });

    // Batch update sessionStorage
    if (Object.keys(sessionUpdates).length > 0) {
        updateFilterState(sessionUpdates as Partial<FilterState>);
    }

    window.history.replaceState({}, '', url.toString());
}

export function getUrlParams(): Record<string, string> {
    const params = new URLSearchParams(window.location.search);
    const result: Record<string, string> = {};
  
    params.forEach((value, key) => {
        result[key] = value;
    });
  
    return result;
}

const parseFacets = (facetsParam: string) => {
    const logger = useLogger('TeamSchedule -> urlParamsUtils');
    try {
        return JSON.parse(facetsParam);
    } catch (e) {
        logger.error('Failed to parse facets from URL', e);
        return null;
    }
};

const parseColumnDisplay = (columnDisplayParam: string) => {
    const logger = useLogger('TeamSchedule -> urlParamsUtils');
    try {
        return JSON.parse(columnDisplayParam);
    } catch (e) {
        logger.error('Failed to parse columnDisplay from URL', e);
        return null;
    }
};

/**
 * Resolve a query field from URL params (with optional parsing) or sessionStorage fallback.
 */
function resolveParam<T>(
    urlValue: string | undefined,
    sessionValue: T | undefined,
    parser?: (v: string) => T | null
): T | undefined {
    if (urlValue) return parser ? (parser(urlValue) ?? undefined) : urlValue as unknown as T;
    return sessionValue;
}

/**
 * Build a query object from URL params + sessionStorage.
 * Supports backward compatibility: if facets are still in the URL (old bookmarks),
 * they will be read from there and migrated to sessionStorage on next write.
 */
export function buildQueryFromUrl(): Partial<Query> {
    const params = getUrlParams();
    const query: Partial<Query> = {};
    const sessionState = loadFilterState();
    
    // Read lightweight params from URL
    const stringParams: Array<keyof typeof params> = [
        'selectedView', 'timezone', 'view.selected'
    ];
    stringParams.forEach((key) => {
        if (params[key]) query[key] = params[key];
    });
    
    if (params.searchType) {
        query.searchType = params.searchType as 'teamSchedule';
    }

    // --- Backward compatibility: read from URL first, fall back to sessionStorage ---
    const resolvedFacets = resolveParam(params.facets, sessionState?.facets, parseFacets);
    if (resolvedFacets !== undefined) {
        query.facets = resolvedFacets;
    }

    const resolvedColumnDisplay = resolveParam(params.columnDisplay, sessionState?.columnDisplay, parseColumnDisplay);
    if (resolvedColumnDisplay !== undefined) {
        query.columnDisplay = resolvedColumnDisplay;
    }

    const resolvedDateRange = resolveParam(params.dateRange, sessionState?.dateRange);
    if (resolvedDateRange !== undefined) {
        query.dateRange = resolvedDateRange;
    }

    const resolvedSearchDateFilter = resolveParam(params.searchDateFilter, sessionState?.searchDateFilter);
    if (resolvedSearchDateFilter !== undefined) {
        query.searchDateFilter = resolvedSearchDateFilter;
    }
    
    return query;
}